import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { syncMixAndMatchCartAttributes } from '@theme/mix-and-match-cart';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @returns {URL}
 */
function getSectionRenderURL() {
  if (/^\/apps\//.test(window.location.pathname)) {
    const root = Theme?.routes?.root || '/';
    return new URL(root, window.location.origin);
  }

  return new URL(window.location.href);
}

/**
 * @param {Node | undefined} node
 * @returns {string | number | undefined}
 */
function getCartRowNodeKey(node) {
  if (node instanceof Element && node.matches('tr[data-key]')) {
    return node.getAttribute('data-key') ?? undefined;
  }

  if (node instanceof Element && node.id) {
    return node.id;
  }

  return undefined;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  /** @type {Promise<void>} */
  #drawerMorphQueue = Promise.resolve();

  /**
   * @returns {string}
   */
  #getShopRoot() {
    if (Theme?.routes?.root) return Theme.routes.root || '/';
    return '/';
  }

  /**
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #getRowByLineIndex(line) {
    const lineString = String(line);
    return (
      this.refs.cartItemRows.find((row) => row.dataset.lineIndex === lineString) ||
      this.refs.cartItemRows[line - 1]
    );
  }

  /**
   * @returns {Set<string>}
   */
  #collectSectionIds() {
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return sectionsToUpdate;
  }

  /**
   * @param {{ mode?: 'hydration' | 'full', injectStylesheet?: boolean }} [options]
   */
  #getCartMorphOptions({ mode = 'hydration', injectStylesheet = false } = {}) {
    return {
      mode: this.isDrawer ? mode : 'full',
      injectStylesheet,
      ...(this.isDrawer ? { getNodeKey: getCartRowNodeKey } : {}),
    };
  }

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: 'full',
            url: getSectionRenderURL(),
          });
        }
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
            url: getSectionRenderURL(),
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Shows the loading overlay while an external add-to-cart is in flight.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartLinesUpdatePending = (event) => {
    if (event.target === this) return;
    if (!this.#isExternalCartUpdate(event)) return;
    if (event.action === 'add') {
      this.#disableCartItems();
    }
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdatePending);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.addEventListener('click', this.#handleRemoveAllUnavailable);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdatePending);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.removeEventListener('click', this.#handleRemoveAllUnavailable);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getRowByLineIndex(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getRowByLineIndex(line);
    if (!cartItemRowToRemove) return;

    const mixMatchId = cartItemRowToRemove.dataset.mixAndMatchId;
    if (mixMatchId) {
      const groupedLineIndices = (cartItemRowToRemove.dataset.mixAndMatchLines || '')
        .split(',')
        .map((value) => parseInt(value.trim(), 10))
        .filter((n) => !Number.isNaN(n));
      const rowBasedLineIndices = this.refs.cartItemRows
        .filter((row) => row.dataset.mixAndMatchId === mixMatchId)
        .map((row) => parseInt(row.dataset.lineIndex ?? '0', 10))
        .filter((n) => !Number.isNaN(n));
      const setLineIndices = groupedLineIndices.length > 0 ? groupedLineIndices : rowBasedLineIndices;
      if (setLineIndices.length > 0) {
        const rowsToRemove = this.refs.cartItemRows.filter((row) => row.dataset.mixAndMatchId === mixMatchId);
        rowsToRemove.forEach((row) => {
          row.style.setProperty('--row-height', `${row.clientHeight}px`);
          row.classList.add('removing');
        });
        return this.#removeMixAndMatchSet(setLineIndices, mixMatchId);
      }
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
        this.#syncDrawerFilledState();
        document.dispatchEvent(new CustomEvent('am:cart-drawer:empty'));
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  /**
   * @param {number[]} lineIndices
   * @param {string} [mixMatchId]
   */
  async #removeMixAndMatchSet(lineIndices, mixMatchId = '') {
    let sorted = [...lineIndices].sort((a, b) => b - a);
    const cartPerformanceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');
    this.#disableCartItems();
    const { cartTotal } = this.refs;

    const sectionsParam = Array.from(this.#collectSectionIds()).join(',');
    let lastSections = null;
    let lastResponse = null;

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineIds = sorted
      .map((line) => this.#getRowByLineIndex(line)?.dataset.key ?? '')
      .filter(Boolean);
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: lineIds.map((id) => ({ id, quantity: 0 })),
        promise: deferredUpdatePromise.promise,
      })
    );

    cartTotal?.shimmer();

    try {
      if (mixMatchId) {
        const root = this.#getShopRoot();
        const cart = await fetch(`${root}cart.js`).then((r) => r.json().catch(() => ({})));
        const liveLines = (cart.items || [])
          .map(
            (
              /** @type {{ properties?: Record<string, string> }} */ item,
              /** @type {number} */ idx
            ) => ({ line: idx + 1, setId: item?.properties?.__MixAndMatchID })
          )
          .filter((/** @type {{ line: number, setId?: string }} */ entry) => entry.setId === mixMatchId)
          .map((/** @type {{ line: number }} */ entry) => entry.line)
          .sort((/** @type {number} */ a, /** @type {number} */ b) => b - a);
        if (liveLines.length > 0) sorted = liveLines;
      }

      for (const line of sorted) {
        const body = JSON.stringify({
          line,
          quantity: 0,
          sections: sectionsParam,
          sections_url: window.location.pathname,
        });
        const res = await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body,
        });
        const text = await res.text();
        const parsed = JSON.parse(text);
        if (parsed.errors) {
          deferredUpdatePromise.reject(new Error(parsed.errors));
          return;
        }
        if (parsed.sections) lastSections = parsed.sections;
        lastResponse = parsed;
      }

      await syncMixAndMatchCartAttributes(lastResponse ?? undefined);
      resetShimmer(this);

      if (lastSections?.[this.sectionId]) {
        const newSectionHTML = new DOMParser().parseFromString(lastSections[this.sectionId], 'text/html');
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        if (lastResponse) {
          this.#updateQuantitySelectors(lastResponse);
        }

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(lastResponse ?? {}),
          detail: {
            sections: lastSections,
            items: lastResponse?.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        this.refs.cartItemRows
          ?.filter((row) => row.classList.contains('removing'))
          .forEach((row) => row.classList.remove('removing'));

        await morphSection(
          this.sectionId,
          lastSections[this.sectionId],
          this.#getCartMorphOptions({ mode: 'full' })
        );

        this.#updateCartQuantitySelectorButtonStates();
        this.#syncDrawerFilledState();
        this.#refreshShippingInfoModalCountry();
      } else {
        deferredUpdatePromise.reject(new Error('Missing cart sections after mix-and-match removal'));
      }
    } catch (error) {
      console.error(error);
      deferredUpdatePromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Failed to remove mix-and-match set',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformanceUpdateMarker);
    }
  }

  /**
   * Handles Remove All (unavailable items) button click.
   * @param {Event} event
   */
  #handleRemoveAllUnavailable = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target ? target.closest('[data-action="remove-all-unavailable"]') : null;
    if (!button || !(button instanceof HTMLButtonElement)) return;

    const raw = button.dataset.lineIndexes;
    if (!raw) return;

    const indexes = raw
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => b - a);

    if (indexes.length === 0) return;

    button.disabled = true;
    try {
      for (const line of indexes) {
        await this.updateQuantity({ line, quantity: 0, action: 'clear' });
      }
    } finally {
      button.disabled = false;
    }
  };

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const sectionsToUpdate = this.#collectSectionIds();

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.#getRowByLineIndex(line)?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        await syncMixAndMatchCartAttributes(parsedResponseText);

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        const applyMorph = async () => {
          await morphSection(
            this.sectionId,
            parsedResponseText.sections[this.sectionId],
            this.#getCartMorphOptions({ mode: 'full' })
          );
          this.#updateCartQuantitySelectorButtonStates();
          this.#syncDrawerFilledState();
          this.#refreshShippingInfoModalCountry();
        };

        if (this.isDrawer) {
          this.#drawerMorphQueue = this.#drawerMorphQueue.catch(() => {}).then(applyMorph);
          await this.#drawerMorphQueue;
        } else {
          await applyMorph();
        }
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const isExternalAdd = event.action === 'add' && this.#isExternalCartUpdate(event);
        const applyUpdate = () => this.#applyCartSections(detail, isExternalAdd);

        if (this.isDrawer && isExternalAdd) {
          this.#drawerMorphQueue = this.#drawerMorphQueue.catch(() => {}).then(applyUpdate);
          await this.#drawerMorphQueue;
          return;
        }

        await applyUpdate();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      })
      .finally(() => {
        this.#enableCartItems();
      });
  };

  /**
   * @param {CartLinesUpdateEvent['detail']} detail
   * @param {boolean} isExternalAdd
   * @returns {Promise<void>}
   */
  async #applyCartSections(detail, isExternalAdd) {
    const sections = detail?.sections;
    const cartItemsHtml = sections?.[this.sectionId];
    const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
    /** @type {'hydration' | 'full'} */
    const mode =
      wasEmptyCartDrawer || (this.isDrawer && isExternalAdd)
        ? 'full'
        : this.isDrawer
          ? 'hydration'
          : 'full';
    const morphOptions = this.#getCartMorphOptions({
      mode,
      injectStylesheet: wasEmptyCartDrawer,
    });

    if (cartItemsHtml) {
      const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

      if (wasEmptyCartDrawer) {
        await startViewTransition(async () => {
          await morphSection(this.sectionId, cartItemsHtml, morphOptions);
        }, ['fill-cart-drawer']);
      } else {
        await morphSection(this.sectionId, cartItemsHtml, morphOptions);
      }

      if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
        for (const row of this.refs.cartItemRows ?? []) {
          if (!existingKeys.has(row.dataset.key)) {
            row.classList.add('adding');
            onAnimationEnd(row, () => row.classList.remove('adding'));
          }
        }
      }

      this.#updateCartQuantitySelectorButtonStates();
      this.#syncDrawerFilledState();
      this.#refreshShippingInfoModalCountry();
      return;
    }

    await sectionRenderer.renderSection(this.sectionId, {
      cache: false,
      ...morphOptions,
      url: getSectionRenderURL(),
    });
    this.#syncDrawerFilledState();
    this.#refreshShippingInfoModalCountry();
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #syncDrawerFilledState() {
    if (!this.isDrawer) return;

    const drawer = this.closest('cart-drawer-component');
    if (drawer && 'syncFilledCartState' in drawer && typeof drawer.syncFilledCartState === 'function') {
      drawer.syncFilledCartState();
    }
  }

  #refreshShippingInfoModalCountry() {
    window.__checkShippingInfoModalCountryMatch?.(true);
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
